import { Easings, interpolateLinearDirectly, interpolateVec3LinearDirectly } from "@scrolly/renderer";
import type { AnimationGroupSerialized } from "@scrolly/post-types";
import { AnimationType } from "@scrolly/post-types";

export class Layer3D {
    position: Vector3Tuple | undefined;
    scale: Vector3Tuple | undefined;
    rotation: Vector3Tuple | undefined;
    opacity: number | undefined;
}

function interpolateAnimations(
    animationsGroup: AnimationGroupSerialized,
    t: number
): Vector3Tuple | undefined | unknown {
    const { type, keyframes } = animationsGroup;
    const firstKeyframe = keyframes[0];
    const lastKeyframe = keyframes[keyframes.length - 1];

    // happy path
    if (firstKeyframe.start >= t) {
        return firstKeyframe.value;
    }
    if (lastKeyframe.start <= t) {
        return lastKeyframe.value;
    }

    // could do a bisect here or create a hash table
    const toKeyframeIndex = keyframes.findIndex((k) => k.start >= t);

    const toKey = keyframes[toKeyframeIndex];

    const fromKey = keyframes[toKeyframeIndex - 1];

    // inline linear interpolation
    const minValue = fromKey.start;
    const maxValue = toKey.start;

    const minScale = 0;
    const maxScale = 1;

    const ratio = (maxScale - minScale) / (maxValue - minValue);
    const easingFunction = Easings[fromKey.easing];
    const timeInterpolated = Math.min(Math.max(minScale, minScale + ratio * (t - minValue)), maxScale);
    const timeEased = easingFunction(timeInterpolated);

    switch (type) {
        case AnimationType.opacity: {
            return interpolateLinearDirectly(fromKey.value as number, toKey.value as number, timeEased);
        }
        case AnimationType.position:
        case AnimationType.rotation:
        case AnimationType.scale: {
            return interpolateVec3LinearDirectly(
                fromKey.value as Vector3Tuple,
                toKey.value as Vector3Tuple,
                timeEased
            );
        }
        default:
            return undefined;
    }
}

export function layer3D(animationGroups: TODOAny, t: number): Layer3D {
    const newLayer: Layer3D = {
        opacity:
            animationGroups.opacity.active && animationGroups.opacity.keyframes.length > 0
                ? interpolateAnimations(animationGroups.opacity, t)
                : animationGroups.opacity.initialValue,
        position:
            animationGroups.position.active && animationGroups.position.keyframes.length > 0
                ? interpolateAnimations(animationGroups.position, t)
                : animationGroups.position.initialValue,
        scale:
            animationGroups.scale.active && animationGroups.scale.keyframes.length > 0
                ? interpolateAnimations(animationGroups.scale, t)
                : animationGroups.scale.initialValue,
        rotation:
            animationGroups.rotation.active && animationGroups.rotation.keyframes.length > 0
                ? interpolateAnimations(animationGroups.rotation, t)
                : animationGroups.rotation.initialValue,
    };
    return newLayer;
}
